/* eslint-disable react/no-danger */
import React, { useState } from 'react';
import _ from 'lodash';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'gatsby';
import BoxedContainer from './layouts/BoxedContainer';
import useMediaQuery from '../hooks/useMediaQuery';
import Sprites from '../components/Sprites';

function FreeZoneServices({ id, data }) {
  // console.log(data);
  const title = _.get(data, 'title', '');
  const text = _.get(data, 'text', '');
  const cta = _.get(data, 'cta', {});
  const ctaLabel = _.get(data, 'ctaLabel', '');
  const listItems = _.get(data, 'listItems', []);
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [activeIndex, setActiveIndex] = useState(0);

  const handleToggle = (index) => {
    setActiveIndex(prevIndex => (prevIndex === index ? null : index));
  };

  return (
    <section id={id} className="py-[50px] lg:py-[100px] bg-desertStorm relative">
      <BoxedContainer
        className="max-w-[90%] lg:!max-w-[1770px] !px-0 lg:!px-6 4xl:!px-0 flex flex-col gap-10 lg:gap-12
      z-10 relative">
        <div className="w-full flex flex-col gap-10 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.4 }}
            viewport={{ once: true }}
            className="flex flex-col items-center gap-5">
            <div
              className="child:text-clamp20to40 child:ltr:!font-URWDINMedium 
              child:rtl:!font-GESSTwoDWTCMedium child:leading-[1.3]
              last:child:!m-0 child:text-center child:text-[#231F20]"
              dangerouslySetInnerHTML={{ __html: title }}
            />
            <div className="py-px bg-red w-10 lg:w-12.5" />
            <div
              className="child:text-clamp12to18 child:ltr:!font-URWDINRegular 
              child:rtl:!font-GESSTwoDWTCLight child:leading-1.4
              last:child:!m-0 child:text-center col-span-6 p-child:text-[#4D4D4D]"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </motion.div>
          <div
            className="flex flex-col md:grid md:grid-cols-12 2xl:grid-cols-10
            gap-11.75 md:gap-6 md:gap-y-16">
            {_.map(listItems, (item, key) => {
              const toggle = activeIndex === key;

              const itemTitle = _.get(item, 'content.title', '');
              const textList = _.get(item, 'content.textList', []);
              const itemIcon = _.get(item, 'content.icon', {});

              return (
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3 * (key + 1) }}
                  key={key}
                  className="col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 2xl:col-span-2
                  flex flex-col md:pb-10 border-t-2 border-red bg-white
                  items-start gap-5 relative px-5 md:px-[30px] pt-5 md:pt-[60px] pb-7">
                  {_.isEmpty(itemIcon) || (
                    <LazyLoadImage
                      src={itemIcon.url}
                      alt={itemTitle}
                      className="h-13.5 w-auto lg:h-20 absolute top-0 left-1/2 transform
                      -translate-x-1/2 -translate-y-1/2"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handleToggle(key)}
                    className="pt-5 md:pt-0 md:pointer-events-none w-full
                    flex justify-between items-center">
                    <span className="ltr:text-clamp14to20 rtl:text-[18px] 
                    ltr:font-LibreBaskervilleRegular rtl:font-GESSTwoDWTCMedium text-[#333]">
                      {itemTitle}
                    </span>
                    <button type="button" className="md:hidden">
                      <Sprites name={toggle ? 'free-zone-arrow-up' : 'free-zone-arrow-down'} />
                    </button>
                  </button>
                  <AnimatePresence>
                    <div
                      data-toggle={isDesktop || toggle}
                      className="grid md:!grid grid-cols-1 gap-4 lg:gap-[22px]
                      data-[toggle=false]:hidden overflow-hidden pt-1 md:overflow-visible">
                      {_.map(textList, (_item, _key) => (
                        <div className="flex h-fit gap-3.5 lg:gap-5 
                        ltr:leading-1.375 
                        rtl:leading-1.4 rtl:md:leading-1.5" 
                        key={_key}>
                          <Sprites
                            name="tick"
                            className="w-[15px] md:w-[25px] min-w-[15px] md:min-w-[25px]
                              h-[15px] md:h-[25px] fill-[#CE445B] relative top-[-2px] lg:top-[-4px]"
                          />
                          <span
                            key={_key}
                            className="rtl:text-clamp12_8to16 ltr:text-clamp11to16 break-normal 
                            ltr:font-URWDINRegular 
                            rtl:font-GESSTwoDWTCLight
                            text-[#4D4D4D]">
                            {_item}
                          </span>
                        </div>
                      ))}
                    </div>
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        </div>
        {_.isEmpty(cta) || (
          <div className="grid grid-cols-1">
            <Link
              to={cta.url}
              className="w-fit flex justify-center items-center rounded-[48px]
              px-12 md:px-14 bg-[#CE445B] hover:bg-[#B53B50] text-white
              uppercase 
              ltr:font-URWDINBold 
              rtl:font-GESSTwoDWTCMedium mx-auto 
              ltr:tracking-[0.96px] leading-1.5 md:leading-1.25
              min-h-[40px] md:h-[51px] text-center py-2
              rtl:text-[12px] ltr:text-[11px] rtl:md:text-[16px] ltr:md:text-[15px] 
              rtl:lg:text-[18px]
              md:py-5 rtl:pt-[10px] ltr:pt-[12px] ltr:md:pt-6 rtl:md:pt-5">
              {cta.name}
            </Link>
          </div>
        )}
      </BoxedContainer>
      <div className="absolute ltr:right-0 bottom-0 hidden lg:flex ltr:justify-end
      rtl:left-0 rtl:scale-[-1]">
        <Sprites name="free-zone-services-arrow" className="w-[80%]" />
      </div>
    </section>
  );
}

export default FreeZoneServices;
